// src/components/sessions/RecordingsDropdown.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { VideoCameraIcon, ChevronDownIcon, XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';

const transformRecordingUrl = (url) => {
  if (!url) return null;
  
  if (url.includes('recordings.simpleclass.eu')) {
    return url;
  }
  
  const match = url.match(/simpleclass-recordings[^\/]*\/(.+)$/);
  if (match) {
    return `https://recordings.simpleclass.eu/${match[1]}`;
  }
  
  return url;
};

const formatTime = (dateStr, locale) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const formatDuration = (seconds, t) => {
  if (!seconds || seconds < 0) return null;
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return t('time.duration.hoursMinutesSeconds', { hours, minutes, seconds: secs });
  } else if (minutes > 0) {
    return t('time.duration.minutesSeconds', { minutes, seconds: secs });
  } else {
    return t('time.duration.seconds', { seconds: secs });
  }
};

const RecordingModal = ({ url, onClose, recordingNumber, totalRecordings, sessionInfo, recordingData }) => {
  const { t, i18n } = useTranslation();

  const getSessionTitle = () => {
    const parts = [];
    
    if (sessionInfo.courseName || sessionInfo.course_name) {
      parts.push(sessionInfo.courseName || sessionInfo.course_name);
    }
    
    const sessionNum = sessionInfo.sessionNumber || sessionInfo.session_number;
    if (sessionNum) {
      parts.push(t('sessions.sessionNumber', { number: sessionNum }));
    }
    
    if (sessionInfo.title && sessionInfo.title !== sessionInfo.courseName) {
      parts.push(sessionInfo.title);
    }
    
    return parts.length > 0 ? parts.join(' • ') : t('recordings.sessionRecording');
  };

  const startTime = formatTime(
    recordingData?.start_time || recordingData?.startTime || recordingData?.created_at,
    i18n.language
  );
  const endTime = formatTime(recordingData?.end_time || recordingData?.endTime, i18n.language);
  const duration = formatDuration(recordingData?.duration, t);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      <div className="w-full h-full max-w-[95vw] max-h-[95vh] flex flex-col">
        <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between w-full mb-2">
            <div className="flex items-center space-x-4 flex-1 min-w-0">
              <h1 className="text-white font-semibold truncate">
                {getSessionTitle()}
              </h1>
              
              <div className="flex items-center space-x-1.5 px-2.5 py-1 bg-red-500/20 rounded-md flex-shrink-0">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-xs font-medium text-red-400">
                  {t('recordings.indicator', { 
                    number: recordingNumber,
                    total: totalRecordings > 1 ? ` ${t('common.of')} ${totalRecordings}` : ''
                  })}
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button
                onClick={onClose}
                className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 transition-colors flex items-center gap-1"
              >
                <XMarkIcon className="w-4 h-4" />
                {t('common.close')}
              </button>
            </div>
          </div>

          {(startTime || endTime || duration) && (
            <div className="flex items-center space-x-4 text-sm text-gray-300">
              {startTime && endTime && (
                <span className="flex items-center">
                  <ClockIcon className="w-4 h-4 mr-1.5" />
                  {startTime} - {endTime}
                </span>
              )}
              {duration && (
                <span className="text-gray-400">
                  {t('recordings.duration')}: {duration}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 bg-black flex items-center justify-center">
          <video
            controls
            autoPlay
            className="w-full h-full"
            style={{ maxHeight: 'calc(95vh - 76px)' }}
          >
            <source src={url} type="video/mp4" />
            {t('recordings.browserNotSupported')}
          </video>
        </div>
      </div>
    </div>
  );
};

const RecordingsDropdown = ({ recordings, sessionInfo = {} }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const sortedRecordings = [...recordings].sort((a, b) => {
    if (a.created_at && b.created_at) {
      return new Date(a.created_at) - new Date(b.created_at);
    }
    if (a.id && b.id) {
      return a.id - b.id;
    }
    return 0;
  });

  const getRecordingUrl = (recording) => {
    const url = recording.url || 
                recording.recording_url || 
                recording.recordingUrl || 
                recording.storage_url ||
                recording.storageUrl;
    
    return transformRecordingUrl(url);
  };

  if (sortedRecordings.length === 1) {
    const url = getRecordingUrl(sortedRecordings[0]);
    
    if (!url) {
      return (
        <button
          disabled
          className="px-3 py-1.5 bg-gray-300 text-gray-500 text-sm rounded-lg flex items-center cursor-not-allowed"
          title={t('recordings.noUrl')}
        >
          <VideoCameraIcon className="w-4 h-4 mr-1.5" />
          {t('recordings.noUrlLabel')}
        </button>
      );
    }

    return (
      <>
        <button
          onClick={() => setSelectedRecording({ 
            url, 
            number: 1, 
            total: 1,
            data: sortedRecordings[0]
          })}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center transition-colors"
        >
          <VideoCameraIcon className="w-4 h-4 mr-1.5" />
          {t('sessions.buttons.recording')}
        </button>
        
        {selectedRecording && (
          <RecordingModal
            url={selectedRecording.url}
            recordingNumber={selectedRecording.number}
            totalRecordings={selectedRecording.total}
            recordingData={selectedRecording.data}
            sessionInfo={sessionInfo}
            onClose={() => setSelectedRecording(null)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center transition-colors"
        >
          <VideoCameraIcon className="w-4 h-4 mr-1.5" />
          {t('recordings.multiple', { count: sortedRecordings.length })}
          <ChevronDownIcon className="w-3 h-3 ml-1" />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-10">
            <div className="py-1">
              {sortedRecordings.map((recording, index) => {
                const url = getRecordingUrl(recording);
                
                if (!url) {
                  return (
                    <div
                      key={recording.id || index}
                      className="px-4 py-2 text-sm text-gray-400 cursor-not-allowed"
                    >
                      {t('recordings.itemNoUrl', { number: index + 1 })}
                    </div>
                  );
                }

                return (
                  <button
                    key={recording.id || index}
                    onClick={() => {
                      setSelectedRecording({ 
                        url, 
                        number: index + 1,
                        total: sortedRecordings.length,
                        data: recording
                      });
                      setIsOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                  >
                    {t('recordings.item', { number: index + 1 })}
                    {recording.duration && (
                      <span className="text-xs text-slate-500 ml-1">
                        ({Math.round(recording.duration / 60)}m)
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {selectedRecording && (
        <RecordingModal
          url={selectedRecording.url}
          recordingNumber={selectedRecording.number}
          totalRecordings={selectedRecording.total}
          recordingData={selectedRecording.data}
          sessionInfo={sessionInfo}
          onClose={() => setSelectedRecording(null)}
        />
      )}
    </>
  );
};

export default RecordingsDropdown;